import { appLogger } from '@bco/app-logger';
import {
  BascAlert,
  BascButton,
  BascCenter,
  BascGroup,
  BascIcon,
  BascStack,
  BascText,
  CommonIcons,
  i18n,
} from '@bco/ui';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface PageErrorBoundaryProps {
  readonly children: ReactNode;
  /**
   * When any value in this array changes between renders, the boundary resets
   * its error state and re-attempts rendering its children. Wire this to the
   * current route so navigating away from a broken page recovers the outlet.
   */
  readonly resetKeys?: readonly unknown[];
  /** Optional custom fallback. When omitted the default recovery UI is shown. */
  readonly fallback?: ReactNode;
}

interface PageErrorBoundaryState {
  readonly hasError: boolean;
  readonly error: Error | null;
}

function areKeysEqual(
  a: readonly unknown[] | undefined,
  b: readonly unknown[] | undefined
): boolean {
  if (a === b) {
    return true;
  }
  if (a == null || b == null || a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => Object.is(value, b[index]));
}

/**
 * Error boundary for a single federated page outlet. A render/commit throw in a
 * remote page is caught here and replaced with a recovery fallback, so the shell
 * chrome (header, nav, footer) keeps rendering instead of the whole app root
 * unmounting to a permanent blank screen.
 *
 * Recovery paths:
 *  - "Reload" resets the boundary state to re-attempt the same page.
 *  - Changing `resetKeys` (e.g. on route change) auto-resets the boundary.
 */
export class PageErrorBoundary extends Component<
  PageErrorBoundaryProps,
  PageErrorBoundaryState
> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: PageErrorBoundaryProps): void {
    if (
      this.state.hasError &&
      !areKeysEqual(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.reset();
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log id/message + componentStack only — never page content or PII.
    appLogger.error('Page render error', {
      error: error.message,
      info: info.componentStack,
    });
  }

  private reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = (): void => {
    this.reset();
    window.location.reload();
  };

  private handleBack = (): void => {
    window.history.back();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback != null) {
      return this.props.fallback;
    }

    return (
      <BascCenter p="xl" style={{ minHeight: '40vh' }}>
        <BascAlert
          role="alert"
          color="red"
          variant="light"
          icon={<BascIcon name={CommonIcons.WARNING} />}
          title={i18n.t('common:errors.pageTitle')}
          maw={520}
        >
          <BascStack gap="md">
            <BascText textRole="body">
              {i18n.t('common:errors.pageBody')}
            </BascText>
            <BascGroup gap="sm">
              <BascButton
                size="sm"
                color="red"
                onClick={this.handleReload}
                style={{ minHeight: 44 }}
              >
                {i18n.t('common:errors.reload')}
              </BascButton>
              <BascButton
                size="sm"
                variant="default"
                onClick={this.handleBack}
                style={{ minHeight: 44 }}
              >
                {i18n.t('common:errors.back')}
              </BascButton>
            </BascGroup>
          </BascStack>
        </BascAlert>
      </BascCenter>
    );
  }
}

export default PageErrorBoundary;
